import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { DEFAULT_SEVERITY_THRESHOLDS, normalizeThresholds } from "./severity";
import type { SeverityThresholds } from "./types";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface SettingsModalProps {
  open: boolean;
  thresholds: SeverityThresholds;
  onChangeThresholds: (thresholds: SeverityThresholds) => void;
  onClose: () => void;
}

export function SettingsModal({
  open,
  thresholds,
  onChangeThresholds,
  onClose,
}: SettingsModalProps) {
  const { t } = useTranslation("dashboard");
  const [draft, setDraft] = useState(thresholds);

  useEffect(() => {
    if (open) setDraft(thresholds);
  }, [open, thresholds]);

  const save = () => {
    onChangeThresholds(normalizeThresholds(draft));
    onClose();
  };

  const reset = () => {
    setDraft(DEFAULT_SEVERITY_THRESHOLDS);
    onChangeThresholds(DEFAULT_SEVERITY_THRESHOLDS);
  };

  return (
    <Dialog open={open} onOpenChange={(value) => { if (!value) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t("settingsModal.title")}</DialogTitle>
          <DialogDescription>
            {t("settingsModal.storedSummary", {
              high: thresholds.high.toFixed(2),
              med: thresholds.med.toFixed(2),
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Label htmlFor="high-threshold" className="cursor-help">
                  {t("settingsModal.highThreshold")}
                </Label>
              </TooltipTrigger>
              <TooltipContent>{t("settingsModal.highThresholdHelp")}</TooltipContent>
            </Tooltip>
            <Input
              aria-label={t("settingsModal.highThreshold")}
              id="high-threshold"
              max="1"
              min="0"
              onChange={(event) => setDraft((current) => ({ ...current, high: Number(event.target.value) }))}
              step="0.01"
              type="number"
              value={draft.high}
            />
          </div>
          <div className="space-y-2">
            <Tooltip>
              <TooltipTrigger asChild>
                <Label htmlFor="medium-threshold" className="cursor-help">
                  {t("settingsModal.medThreshold")}
                </Label>
              </TooltipTrigger>
              <TooltipContent>{t("settingsModal.medThresholdHelp")}</TooltipContent>
            </Tooltip>
            <Input
              aria-label={t("settingsModal.medThreshold")}
              id="medium-threshold"
              max="1"
              min="0"
              onChange={(event) => setDraft((current) => ({ ...current, med: Number(event.target.value) }))}
              step="0.01"
              type="number"
              value={draft.med}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={reset} type="button">
            {t("settingsModal.reset")}
          </Button>
          <Button onClick={save} type="button">
            {t("settingsModal.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
